import { useSyncExternalStore } from "react";

/**
 * Persistent history store for toast notifications. Every toast that flows
 * through @/lib/toast lands here so users can scroll back through pop-ups
 * they missed while looking away from the screen.
 *
 * Backed by localStorage so the history (and unread count) survive page
 * reloads. Capped at MAX_ENTRIES newest-first to keep storage bounded.
 *
 * Listeners use a manual subscribe + useSyncExternalStore pattern instead
 * of React state — recording a toast from inside another render's effect
 * needs to not trigger setState-during-render warnings.
 */

const STORAGE_KEY = "fm.toastHistory.v1";
const MAX_ENTRIES = 100;

export type Severity = "success" | "warning" | "error" | "info";

export interface ToastHistoryEntry {
  id: string;
  ts: number;
  severity: Severity;
  message: string;
  description?: string;
}

interface ToastHistoryState {
  entries: ToastHistoryEntry[];
  unreadCount: number;
}

function load(): ToastHistoryState {
  if (typeof localStorage === "undefined") return { entries: [], unreadCount: 0 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { entries: [], unreadCount: 0 };
    const parsed = JSON.parse(raw) as Partial<ToastHistoryState>;
    return {
      entries: Array.isArray(parsed.entries)
        ? parsed.entries.slice(0, MAX_ENTRIES)
        : [],
      unreadCount:
        typeof parsed.unreadCount === "number" && parsed.unreadCount >= 0
          ? parsed.unreadCount
          : 0,
    };
  } catch {
    return { entries: [], unreadCount: 0 };
  }
}

let state: ToastHistoryState = load();
const listeners = new Set<() => void>();

function save() {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage quota / disabled — silently give up; the in-memory
       state is still authoritative for this session. */
  }
}

function notify() {
  for (const l of listeners) l();
}

export function recordToast(
  severity: Severity,
  message: string,
  description?: string,
): void {
  // Guard against junk values from caller (Sonner accepts non-string messages
  // in some forms; we coerce to string for storage).
  const msg = typeof message === "string" ? message : String(message ?? "");
  const entry: ToastHistoryEntry = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    ts: Date.now(),
    severity,
    message: msg,
    description,
  };
  state = {
    entries: [entry, ...state.entries].slice(0, MAX_ENTRIES),
    unreadCount: state.unreadCount + 1,
  };
  save();
  notify();
}

export function clearAll(): void {
  state = { entries: [], unreadCount: 0 };
  save();
  notify();
}

export function markAllRead(): void {
  if (state.unreadCount === 0) return;
  state = { ...state, unreadCount: 0 };
  save();
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ToastHistoryState {
  return state;
}

/**
 * Subscribe a React component to the history. Re-renders on every
 * recordToast / clearAll / markAllRead.
 */
export function useToastHistory(): ToastHistoryState {
  // useSyncExternalStore is the React 18 pattern for external stores —
  // tear-free across concurrent renders, and the same snapshot reference
  // means consumers don't re-render unless state actually changed.
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Cross-tab sync: when another tab updates the history (or a manual
// localStorage edit), reload + notify so the badge stays in sync.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    state = load();
    notify();
  });
}

// Re-exported for tests that want to seed/inspect state without touching
// localStorage directly. Not part of the public API for app code.
export const __test = { load, save };
